import React, { useRef, useEffect } from 'react';
import { AuditTimelineEvent } from '../../../interfaces/Audit';
import '../planning-comparison/PlanningComparisonModal.css';
import '../../../shared/ui/modal-close-button.css'; // X padrão do sistema

interface LiberacaoDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  event: AuditTimelineEvent | null;
}

const LiberacaoDetailsModal: React.FC<LiberacaoDetailsModalProps> = ({
  isOpen,
  onClose,
  event,
}) => {
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleMouseDown = (e: MouseEvent) => {
      if (modalRef.current && !modalRef.current.contains(e.target as Node)) {
        onClose();
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleMouseDown);
    }
    return () => {
      document.removeEventListener('mousedown', handleMouseDown);
    };
  }, [isOpen, onClose]);

  if (!isOpen || !event) return null;

  const metadata = event.metadata || {};
  const hasLiberacaoMetadata = metadata.liberacao === true;

  const performer = event.collaboratorNames.length > 0
    ? event.collaboratorNames.join(', ')
    : metadata.performedByName || 'Sistema';

  const dateStr = new Date(event.occurredAt).toLocaleDateString('pt-BR');
  const timeStr = new Date(event.occurredAt).toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  const totalChange = metadata.totalChange; // 'CONCEDIDA' | 'REMOVIDA' | null
  const addedEsteiras = metadata.addedEsteiras || [];
  const removedEsteiras = metadata.removedEsteiras || [];
  const note = metadata.note;
  // Sessões derrubadas pela remoção (at-44). Registros anteriores não têm o campo.
  const sessoesEncerradas = metadata.sessoesEncerradas || [];

  return (
    <div className="planning-modal-overlay">
      <div className="planning-modal-content" ref={modalRef}>
        <div className="planning-modal-header">
          <h2>Histórico da Liberação Administrativa</h2>
          <button className="close-button" onClick={onClose} aria-label="Fechar">
            &times;
          </button>
        </div>

        <div className="planning-modal-metadata">
          <div className="metadata-item">
            <strong>Alterado por:</strong> <span>{performer}</span>
          </div>
          <div className="metadata-item">
            <strong>Data da alteração:</strong> <span>{dateStr} às {timeStr}</span>
          </div>
        </div>

        <div className="planning-modal-body">
          {!hasLiberacaoMetadata ? (
            <div className="planning-modal-fallback">
              <p className="fallback-title">{event.title}</p>
              <p className="fallback-desc">{event.description || 'Sem descrição complementar.'}</p>
              <p className="fallback-note">
                Este registro refere-se a uma alteração legada de liberação e não contém metadados estruturados.
              </p>
            </div>
          ) : (
            <div className="planning-comparison-grid single-column">
              <div className="planning-column">
                <div className="column-header">Modificações na Liberação</div>
                <div className="status-flow-list">
                  {/* Liberação Total */}
                  {totalChange && (
                    <div className="status-flow-item item-unchanged">
                      <div className="item-index">•</div>
                      <div className="item-label" style={{ fontWeight: 600 }}>
                        Liberação Total
                      </div>
                      <span className={`badge-change ${totalChange === 'CONCEDIDA' ? 'font-added' : 'font-removed'}`}>
                        {totalChange === 'CONCEDIDA' ? 'Concedida' : 'Removida'}
                      </span>
                    </div>
                  )}

                  {/* at-66 (BUG-V58): a numeração é contínua nas duas listas. Cada uma
                      reiniciava em 1, então uma liberação que concedia 2 esteiras e removia
                      2 exibia "1, 2, 1, 2" numa lista visualmente única. */}
                  {/* Added Esteiras */}
                  {addedEsteiras.map((est: any, index: number) => (
                    <div key={`added-${est.id}-${index}`} className="status-flow-item item-added">
                      <div className="item-index">{index + 1}</div>
                      <div className="item-label">
                        {est.nome}
                      </div>
                      <span className="badge-change font-added">Liberada</span>
                    </div>
                  ))}

                  {/* Removed Esteiras */}
                  {removedEsteiras.map((est: any, index: number) => (
                    <div key={`removed-${est.id}-${index}`} className="status-flow-item item-removed">
                      <div className="item-index">{addedEsteiras.length + index + 1}</div>
                      <div className="item-label">
                        {est.nome}
                      </div>
                      <span className="badge-change font-removed">Removida</span>
                    </div>
                  ))}
                </div>

                {/* Atividades encerradas pela remoção (at-44) */}
                {sessoesEncerradas.length > 0 && (
                  <div style={{ marginTop: '16px', padding: '12px 16px', background: 'var(--tema-fundo, #fffbeb)', border: '1px solid var(--tema-borda-amarelo, #fde68a)', borderRadius: '12px' }}>
                    <strong style={{ display: 'block', fontSize: '13px', color: 'var(--tema-texto-laranja, #78350f)', fontFamily: 'AmpleSoft-Bold, sans-serif', marginBottom: '6px' }}>
                      Atividades encerradas por esta remoção:
                    </strong>
                    <ul style={{ margin: 0, paddingLeft: '18px', fontSize: '13.5px', color: 'var(--tema-texto-laranja, #78350f)' }}>
                      {sessoesEncerradas.map((s: any, index: number) => (
                        <li key={`sessao-${index}`} style={{ marginBottom: '2px' }}>
                          {s.colaborador}{s.esteira ? ` — ${s.esteira}` : ''}
                        </li>
                      ))}
                    </ul>
                    <span style={{ display: 'block', marginTop: '6px', fontSize: '12.5px', color: 'var(--tema-texto-laranja, #92400e)' }}>
                      As horas trabalhadas foram preservadas e enviadas para aprovação do Chefe de Oficina.
                    </span>
                  </div>
                )}

                {/* Observação / Nota */}
                {note !== undefined && (
                  <div style={{ marginTop: '16px', padding: '12px 16px', background: 'var(--tema-superficie-2, #f8fafc)', border: '1px solid var(--tema-linha, #e2e8f0)', borderRadius: '12px' }}>
                    <strong style={{ display: 'block', fontSize: '13px', color: 'var(--tema-texto, #1e293b)', fontFamily: 'AmpleSoft-Bold, sans-serif', marginBottom: '4px' }}>
                      Observação:
                    </strong>
                    <span style={{ fontSize: '13.5px', color: 'var(--tema-texto-2, #475569)', fontStyle: note ? 'normal' : 'italic' }}>
                      {note || '-'}
                    </span>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default LiberacaoDetailsModal;
